// ゲームバランス定数 ― 数値調整はすべてここで行う。
// 時間は秒単位（s）、ミリ秒が必要な場面は ms と接尾辞をつける。

export const TICK_HZ = 60
export const FIXED_DT = 1 / TICK_HZ

export const ARENA = {
  width: 960,
  height: 540,
  floorY: 440,
  leftWall: 60,
  rightWall: 900,
} as const

// ボス別アリーナ境界。 平面背景の前景物に乗らないよう「移動範囲」だけ per-boss で絞る。
// 未登録ボスは ARENA 既定(60/900)。 値は仮=実機で調整予定。
export const ARENA_BOUNDS_BY_BOSS: Record<string, { leftWall: number; rightWall: number }> = {
  'vigil-knight': { leftWall: 160, rightWall: 860 },
  // 花嫁: 婚礼の間(v2) の平らな床は全幅だが、 端の前景柱に乗り込まない+右奥の常夜燈(x881)に被らないよう軽く内側へ。 実機で微調整。
  'bride': { leftWall: 100, rightWall: 852 },
}

// 舞台を広げた時の可動域。 🔴 1倍の値を倍率で引き伸ばすと、絵に描かれた柱の位置と
//   無関係に決まってしまい、両端で柱の中に立つ (2026-07-28 実測)。
//   値は「柱の縁に体が10pxかかる」位置。 実機で詰める1つのツマミ。
//   ⚠️ **mul を必ず持つこと。** 値は「その倍率での部屋の貼り位置」から逆算したもので、
//     倍率が変われば柱のワールド座標も動く = 他の倍率へ流用したら意味が壊れる。
//     計器 (tests/combat_sim/arena_width_sweep.test.ts) は 1.3/2/3 倍も掃くので、
//     mul を見ずに返すと**計器の数字だけが黙って変わる**。
const ARENA_BOUNDS_WIDE_BY_BOSS: Record<string, { mul: number; leftWall: number; rightWall: number }> = {
  // 2026-07-28: 'vigil-knight' 行を削除 (knight-hall-v2 統合票 §3-4)。
  //   新背景は世界1608pxぶんしかなく舞台1.4倍(世界1940px)を覆えないため、
  //   舞台1.4倍(main.ts STAGE_MULS)自体を畳んだ。 表と仕組みは他の部屋のために残す。
}

// ---- アリーナ幅の実験倍率 (2026-07-27・カメラ検討) ----
// 「舞台を広くしてカメラで追う」構図を実機で判定するためのツマミ。
//   ⚠️ **既定は 1 = 従来と完全に同一。** 1 の時は下の関数がすべて元の値をそのまま返す
//      (丸めも入らない) ので、production の見え方も挙動も 1px も変わらない。
//   ⚠️ 1 以外にできるのは `import.meta.env.DEV` 配下の呼び出し元だけ (main.ts の DEV キー)。
//   広げ方は「中心を据え置いて左右対称に伸ばす」。未登録倍率の可動域と壁判定は同じ倍率で伸びる。
//   騎士の1.4倍だけは描かれた柱に合わせた専用可動域を使い、arenaWalls() は従来どおり倍率で伸ばす。
const ARENA_WIDTH_MUL_MAX = 3
let arenaWidthMul = 1
export function getArenaWidthMul(): number { return arenaWidthMul }
export function setArenaWidthMul(mul: number): void {
  arenaWidthMul = Math.max(1, Math.min(ARENA_WIDTH_MUL_MAX, mul))
}
// カメラが世界端より外へ見てもよい余白。既定は半画面 = 変更前と完全に同一。
let cameraMarginPx = ARENA.width / 2
export function getCameraMarginPx(): number { return cameraMarginPx }
export function setCameraMarginPx(px: number): void {
  cameraMarginPx = Math.max(0, px)
}
function scaleAbout(center: number, v: number): number {
  return arenaWidthMul === 1 ? v : Math.round(center + (v - center) * arenaWidthMul)
}

// 🔴 廃止 (2026-09-06)＝ PAIR_LEASH_PX (二人が離れられる最大距離・旧560px)。
//   Nine Sols の根拠は最終ボス Eigong 戦 (壁の無い特殊な場) 限定で、通常ボス戦は実壁で
//   閉じていた。本作の部屋 (全ボス最大840px) は画面幅 (960px) より狭いので、
//   「二人が同時に画面外へ出る」事態は壁だけで構造的に起きない＝縄は壁と同じ仕事を
//   見えない形で二重にやっていただけで、実プレイでは「見えない壁」の理不尽として現れた。
//   出所＝docs/candidates/2026-09-06-camera-smash-style-fable-design.md §4-2。

export function arenaBoundsFor(bossId: string | undefined): { leftWall: number; rightWall: number } {
  const o = bossId ? ARENA_BOUNDS_BY_BOSS[bossId] : undefined
  const base = o ? o : { leftWall: ARENA.leftWall, rightWall: ARENA.rightWall }
  if (arenaWidthMul === 1) return base
  const wide = bossId ? ARENA_BOUNDS_WIDE_BY_BOSS[bossId] : undefined
  if (wide && arenaWidthMul === wide.mul) return { leftWall: wide.leftWall, rightWall: wide.rightWall }
  const c = (base.leftWall + base.rightWall) / 2
  return { leftWall: scaleAbout(c, base.leftWall), rightWall: scaleAbout(c, base.rightWall) }
}

// ボスの体が画面の外へはみ出ないよう、ボスだけを壁の手前で止める幅 (px)。主人公は壁まで行ける。
//   2026-10-02 AI点検の候補1「壁際でボスが画面の外へ半分はみ出る」(介錯の外套・溜めの屈みが画面端で切れていた)。
//   部屋の幅 (arenaBoundsFor) を狭めると主人公の前に見えない壁ができるので、ボス側だけを止める。戻す＝行を消す。
// 騎士 = 25 (2026-10-08 ユーザー採用 W-1「壁の手前で止まる (25px)・こちらが良い」)。右だけ余白が狭く、薙ぎの当たりで右 21px 出ていた件。
export const KNIGHT_WALL_INSET_DEFAULT_PX = 25
// 花嫁 = 37 (2026-10-08 ユーザー採用「手前で止める」・翼を開いた絵が画面の外へ出ていた件)。
export const BRIDE_WALL_INSET_DEFAULT_PX = 37
// 覚醒王 = 100 (2026-10-08 ユーザー採用 検収 F-1「止める版・こちらがいい」)・戴冠獣 = 120 (同 F-1b)。
export const AWOKEN_WALL_INSET_DEFAULT_PX = 100
export const HOUND_WALL_INSET_DEFAULT_PX = 120
export const BOSS_BODY_WALL_INSET_PX: Record<string, number> = {
  'the-executioner': 50,
  'bride': BRIDE_WALL_INSET_DEFAULT_PX,
  'vigil-knight': KNIGHT_WALL_INSET_DEFAULT_PX,
  'crowned-king-awoken': AWOKEN_WALL_INSET_DEFAULT_PX,
  'dream-hound': HOUND_WALL_INSET_DEFAULT_PX,
}
// 覚醒王の壁の手前で止める件 (2026-10-09 AI の遊び点検 R1・2026-10-08 ユーザー採用 F-1＝配布版の既定 100・DEV `?awokenWall=<px>` で上書き・`=0` で旧＝部屋の壁まで行く)。
//   壁際で頭と前脚が画面の外へ出たまま次の技が来る件。100 で待機の頭の先 (前 157) が画面の左端の内側 (x≈3)。
//   上限 106 = 大暴れの壁しがみつき (awoken_rampage_choreo.ts WALL_FOOT_INSET_PX) と旧の壁跳ねの壁の位置。これを超えるとしがみつきが壁から浮く。
//   0 = 登録を消す (旧＝壁まで行く)。main.ts の DEV の箱だけが呼ぶ。根拠 = docs/research/2026-10-09-awoken-fresh-eyes.md R1。
export const AWOKEN_WALL_INSET_MAX_PX = 106
export function setAwokenWallInsetTrial(px: number): void {
  setBossWallInsetTrial('crowned-king-awoken', px, AWOKEN_WALL_INSET_MAX_PX)
}
// 戴冠獣の壁の手前で止める件 (2026-10-09 AI の遊び点検 H1・2026-10-08 ユーザー採用 F-1b＝配布版の既定 120・DEV `?houndWall=<px>` で上書き・`=0` で旧)。候補 92／120／136。
//   92 = 待機 (半幅 152) が収まる / 120 = 突進牙の当たり・戻り (179・184)・前脚の払い・三連が収まる / 136 = 赤の掴みの溜め (196) まで。
//   上限 136 = 噛みつき固めの届き 140 がまだ壁際の主人公へ届く所 (研究文書の干渉の確かめ)。根拠 = docs/research/2026-10-09-hound-fresh-eyes.md H1。
export const HOUND_WALL_INSET_MAX_PX = 136
export function setHoundWallInsetTrial(px: number): void {
  setBossWallInsetTrial('dream-hound', px, HOUND_WALL_INSET_MAX_PX)
}
// 花嫁・騎士の壁の手前で止める件 (2026-10-08 AI 点検 C-8)。どちらも 2026-10-08 ユーザー採用＝配布版の既定 (花嫁 37・騎士 25)。
//   DEV `?brideWall=<px>` / `?knightWall=<px>` で上書き・`=0` で旧 (壁まで行く) に戻せる。
//   花嫁は壁ぴったり (100／852) まで行き、翼を開いた絵 (前 137) が左 33・右 25px 画面の外へ出る → 推奨 37 (全ての絵が収まる下限)。
//     シム: 普通に戦う場面は 0〜37 で全く同じ。壁に張りつかれた時だけ薙ぎの回数が約3割減る (理由は未確認)。上限 50 (37 と挙動が同じ)。
//   騎士は右だけ余白が狭く (100)、薙ぎの当たりで右 21px 出る → 推奨 25 (突きの溜め切り 145 まで収めるなら 45)。シムは 45 まで全く同じ・60 から変わる。
//   根拠 = docs/research/2026-10-08-wall-inset-embrace-measure.md §1。
export const BRIDE_WALL_INSET_MAX_PX = 50
export function setBrideWallInsetTrial(px: number): void {
  setBossWallInsetTrial('bride', px, BRIDE_WALL_INSET_MAX_PX)
}
export const KNIGHT_WALL_INSET_MAX_PX = 45
export function setKnightWallInsetTrial(px: number): void {
  setBossWallInsetTrial('vigil-knight', px, KNIGHT_WALL_INSET_MAX_PX)
}
function setBossWallInsetTrial(bossId: string, px: number, max: number): void {
  if (!import.meta.env.DEV) return
  const v = Number.isFinite(px) ? Math.round(Math.max(0, Math.min(max, px))) : 0
  if (v > 0) BOSS_BODY_WALL_INSET_PX[bossId] = v
  else delete BOSS_BODY_WALL_INSET_PX[bossId]
}
export function bossBodyBoundsFor(bossId: string | undefined): { leftWall: number; rightWall: number } {
  const b = arenaBoundsFor(bossId)
  const inset = bossId ? BOSS_BODY_WALL_INSET_PX[bossId] ?? 0 : 0
  return inset > 0 ? { leftWall: b.leftWall + inset, rightWall: b.rightWall - inset } : b
}

// ボスに依らない「舞台の壁」(ARENA 既定 60/900) を倍率込みで返す。
//   壁際判定 (WALL_BAND) / 中央復帰 / アリーナ絶対座標のハザードが参照する。
//   ⚠️ per-boss 境界 (arenaBoundsFor) とは別物。ここを per-boss に変えると
//      「壁際でだけ出る連」の発動条件が現状から動く = 技の選択の再調整 (別作業) になる。
export function arenaWalls(): { leftWall: number; rightWall: number } {
  const c = (ARENA.leftWall + ARENA.rightWall) / 2
  return { leftWall: scaleAbout(c, ARENA.leftWall), rightWall: scaleAbout(c, ARENA.rightWall) }
}
// アリーナ絶対座標 (hazardSpawns の anchor:'arena') を倍率込みの位置へ写す。
//   ⚠️ **幅は写さない (呼び出し側で掛けない)。** 帯の太さを据え置くと「帯の中央に立った時に
//      安全地帯まで歩く距離」が変わらない = 床攻撃の公平性 (tests/hazard_escape) が自動で保たれる。
//      広い部屋を帯で埋め直す再設計は別作業 (docs/candidates の調査 §2-3)。
export function arenaAnchorX(offset: number): number {
  return scaleAbout((ARENA.leftWall + ARENA.rightWall) / 2, offset)
}

// カメラが映してよい世界の範囲。 倍率 1 なら画面 (0..ARENA.width) と完全に一致する
// = カメラを有効にしても画面が動かない (＝従来の見え方)。
export function worldBoundsFor(bossId: string | undefined): { left: number; right: number } {
  const b = arenaBoundsFor(bossId)
  // 🔴 余白は **画面の半分**。 60px だと、壁際に立った人物をカメラが中央へ持ってこられない
  //    (カメラが世界の端で止まり、人物が画面端に張り付く = 直そうとしていた壁ハメの見え方に戻る)。
  //    半画面あれば「壁に背をつけた人物」もちょうど画面中央に来る (2026-07-27 実測で確定)。
  // 2026-08-02: 可変にした。240px = 壁際で人物が中央まで来ないが、舞台1.4倍を騎士の現行背景
  //   (1608px)で覆えるようになる。ユーザー裁定で寄りは許容。
  const margin = cameraMarginPx
  return {
    left: Math.min(0, b.leftWall - margin),
    right: Math.max(ARENA.width, b.rightWall + margin),
  }
}

// 全キャラの表示サイズ基準 ("ものさし")。 主人公 LUNAIRE の表示高さ (px, ARENA 座標)。
// ボス等 他キャラのサイズはこの値への倍率 (BOSS_HEIGHT_RATIO 等) で決める。
// 画面縦 540 の約 22% = 小さめ (Hollow Knight / ENDER LILIES 系)。
// 狙い: 上空をボスの大技・画面縦を使う攻撃用に空けておく。 詳細は docs の size-chart。
export const PLAYER_DISPLAY_HEIGHT = 120

export const PLAYER = {
  width: 30,
  height: 70,
  // バランス哲学:
  //   HP 100 / 表示は命灯5つ (1灯=20HP)。 ボス処刑1発=60HP=命灯3個分。
  //   気絶 = 致死の入口。気絶中にボスの固有フィニッシャーが入り瀕死に
  maxHp: 100,
  lifeLampCount: 5,
  maxPosture: 100,
  // ②本物のSEKIRO化 (2026-07-16 Part3): プレイヤー体幹回復もHP率に連動 (posture.ts
  //   playerPostureRecoveryMult 参照)。 満タンHPでは今より速く (7→11=ミスが連鎖しにくい) /
  //   瀕死ではfloorMul倍まで鈍る (SEKIRO本家は瀕死≈1%だが、 死のスパイラルを避けるため
  //   プレイヤー側は0.25=ボスの0.05より緩め。 低HPの理不尽さは実機で要検証)。
  postureRecoveryPerSec: 11,
  postureRecoveryFloorMul: 0.25,

  // 歩き 120px/s = 正式採用 (2026-06-11 ユーザー裁定・戦闘部レビュー完了)。
  //   優雅な歩きアニメを壊さないため据え置き。 とっさの移動は回避 (step) が担う。
  //   床攻撃の公平性はこの値を前提に tests/hazard_escape.test.ts が自動担保
  //   (「回避1回+歩きで予告の8割以内に逃げ切れる」)。 この値を変えたらテストが全ボスを再検査する。
  moveSpeed: 120,
  // 攻撃リーチは「ボスの見た目の半幅」を吸収する想定で広め
  // (ボス sprite 380px → 半幅 190px のうち実質的な体は半分強なので、120 で「ボスのフチに届く」感)
  attackRange: 160,
  attackActiveStartMs: 60,
  attackActiveEndMs: 140,
  attackRecoveryMs: 260,
  // 攻撃を振った判断に短い責任を持たせる。振り始める前 (< attackActiveStartMs) は
  // パリィへ切り替えられるが、active に入った後はこの時刻まで切り替え不可。
  // active 終了140msから120msだけ追加で拘束し、残り140msの recovery は従来どおり
  // パリィで取り消せる。2026-07-17 ユーザー壁打ちで方針承認、王で先行検証。
  attackParryCancelReopenMs: 260,
  //   ⚠️ 2026-10-09 確かめた事実: 連撃 (既定のテンポ B) では player_attack_timing.ts の値が使われる
  //     (一段目 = 0.16 秒まで切り替え可・0.16〜0.28 秒は不可・三段目は 0.34〜0.46 秒が不可)。上の 60/260 は旧の1段の値。
  // 通常攻撃の連撃 (2026-10-01 ユーザー「3段・突き主体・最後だけ重い・パリィ切替はどの段も今と同じ」)。
  //   攻撃が終わって (idle に戻って) から attackComboGapMs 以内に次を押すと次の段。 3段目の後は1段目へ。
  //   受付 (0〜60ms はパリィへ切替可・60〜260ms は不可・260ms〜 は可) と当たりの窓 (60〜140ms) は全段共通。
  attackComboSteps: 3,
  attackComboGapMs: 300,
  //   連撃を試す間 (DEV ?comboTrial=1) の時間割は src/player_attack_timing.ts の3案 (A/B/C)。
  //   2026-10-02: 220ms で繋ぐ案はユーザー「早すぎるな」で取り下げ。
  //   3段目 (重い締め) だけ: 振り終わりの隙が少し長く、当たりの窓までに前へ踏み込む。
  //   ダメージ・体幹の量は全段同じ (重さは踏み込み・命中の止まり・揺れ・音で出す。比較版 = DEV ?combo3rd=<百分率>・src/player_combo3_power.ts)。
  //   ⚠️ attackHeavyExtraRecoveryMs は今どこからも読まれない (3段目の隙は player_attack_timing.ts の時間割 B = 振り終わりまで 1000ms)。
  attackHeavyExtraRecoveryMs: 120,
  attackHeavyLungePx: 36,

  parryStartupMs: 0,
  parryActiveMs: 200,
  // P1「手の解放」(2026-06-12 ブループリント §2.1): 1サイクル320ms=連撃350ms間隔ビートに
  //   連続対応できる下限。旧180では弾き弾き弾きが間に合わなかった。
  parryRecoveryMs: 120,

  // 前方ダッシュ（旧バックステップから変更）
  // facing方向に滑り込む。2026-09-18 以降は身体判定なし (BOSS_CORE.enabled=false) なのでボスを通り抜ける。
  stepDistance: 90,
  stepDurationMs: 260,
  // hit-timeline (2026-06-11): 回避1回で「短いヒット (activeMs ≤180)」の窓を丸ごと跨げる開始値。
  //   ヒットは i-frame で消えなくなった (持続する) ため、旧130のままだと短い突きすら凌ぎ切れない。
  //   実機調整 knob (spec §6)。ゾーンの持続 (450-700ms) は依然これより遥かに長い = 位置どりは不変。
  stepIFrameMs: 200,
  // 回避の連打対策 (Fable 5.1 設計 2026-09-06・docs/candidates/2026-09-06-dodge-spam-fable-design.md)。
  //   回避の**開始**からこの時間は次の回避を受け付けない (縛るのは回避→回避だけ。攻撃・パリィ・歩きは
  //   従来どおり回避明け=stepDurationMsで出せる)。 いまの連打は1周283msのうち無敵200ms=71%が無敵で、
  //   500msにすると1周500ms・無敵率40%へ落ちる。 500は「いまの譜面で最短の赤→赤 (介錯 exec-maul-jab-jab
  //   =770ms) の窓を1msも削らない上限」(設計書 §3-2)。 拒否した押しは consume しない/歩き・攻撃・パリィは
  //   その tick でも出る (player.ts idle分岐)。 stepDistance/stepDurationMs/stepIFrameMs は不変。
  stepCooldownMs: 500,

  stunMs: 1800,  // 気絶中にボスがフィニッシャーを撃つ余裕を確保

  inputBufferFrames: 5,

  // ジャストドッジ → カウンター窓
  counterWindowMs: 550,

  // カウンター攻撃 (突進)
  thrustSlideSpeed: 1500,
  counterThrustRange: 220,

  // 🔴 スタミナは廃止 (2026-07-27 ユーザー裁定)。
  //   「攻撃/回避のコスト」「息切れ(exhausted)硬直」は、以前の設計で消えたあとも定数と
  //   状態だけが残り、どこからも呼ばれないまま存在していた。体幹だけで戦うのが現行設計。
  //   復活させる場合は、ここへ戻すのではなく設計から起こし直すこと。
  //   旧定数 (maxStamina / staminaRegenPerSec / attackStaminaCost / dodgeStaminaCost /
  //   parryStaminaCost / exhausted*) は git 履歴 (この commit の親) にある。

  // ジャスト/通常パリィ分岐 (2026-06-17)
  perfectParryWindowMs: 80,          // パリィ開始からこのms以内に吸収=ジャストパリィ
  // 2026-06-20 plan A: 22→14。通常受けの連撃で即気絶しないよう緩和 (体幹が溜まる主因はここ)。
  //   ジャスト(6)との差は残し「精密に弾くほど崩れにくい」誘導は維持。
  normalParrySelfPostureGain: 14,    // 通常パリィの自体幹増加 (v1 の素の値。実効は econ.ts の v2 が上書き＝docs/generated/econ-current-values.md)
  perfectParrySelfPostureGain: 6,    // ジャストパリィの自体幹増加 (通常より小さい=踏みとどまりやすい)

  // 体幹回復停止 (2026-06-18): パリィ/被弾でこの秒数は自然回復しない。
  // ジャストは短め・通常は長め = 踏みとどまれるかの緊張感を作る。
  // ※空振りは体幹に触れない方針 (plan A) = 回復停止も課さない。
  perfectParryRegenLockSec: 0.8,
  normalParryRegenLockSec: 1.7,
  hitRegenLockSec: 1.5,

  // パリィ空振り (1発も吸収せず終了) の罰: 体幹は増やさず軽い硬直のみ (2026-06-20 plan A)。
  //   空気を斬っただけで体幹が溜まるのは直感に反する + 連打抑止は窓縮み(下記)とこの硬直で足りる。
  //   parrying 状態をこのms延長して即座の再パリィを封じる (成功時の parryRecoveryMs に上乗せ)。
  wastedParryStiffnessMs: 150,

  // アンチ連打パリィ (SEKIRO 型): 連打で受付窓が縮む。
  //   単発 or 間隔≥mashThresholdMs = フル200ms維持。
  //   パニック連打は窓が parryFloorMs (≈4f@60fps) まで段階的に潰れる。
  //   parryActiveMs(200) 自体は据え置き = 正常タイミングでフル窓が維持される。
  mashThresholdMs: 220,       // これより短い間隔の連打をペナルティ対象とする
  //   ⚠️ 2026-10-09 確かめた事実: 実際の操作ではこの縮みは起きない。空振りのパリィは 0.47 秒 (受付 200＋戻り 120＋空振りの硬直 150) 終わらず、
  //     その間の押しは出ないので「前の押しから 220ms 以内に次のパリィが始まる」ことが無い (決まった直後の出し直しは対象外)。
  //     連打を咎めているのは空振りの硬直の方。tests/defeat_tips_facts.test.ts [whiff]。
  parryShrinkPerPress: 70,    // 連打1発ごとに縮む量 (200→130→67=floor)
  parryFloorMs: 67,           // 受付窓の下限 ≈ 4f@60fps = SEKIRO の床
} as const

export const BOSS = {
  width: 110,
  height: 96,
  maxHp: 100,
  maxPosture: 100,
  // 体幹回復は「幕HP残量に比例」(combat-feel-v2 §3・SEKIRO型)。
  //   実効回復 = postureRecoveryPerSec × (floorMul + (1-floorMul) × 幕HP率)。
  //   満タン 7/s (パリィだけの速攻では割れない) → 削り切り際 ~0.7/s (弾きが確実に積み上がる)。
  //   = 「先に削って体幹を固定し、 弾きで割る」の攻防の順序を作る。
  postureRecoveryPerSec: 7,
  // 本物のSEKIRO型移行 (2026-07-15 A4): 0.1→0.05。 公式wikiの実数表 (HP25%以下で回復≈1%)
  //   に寄せ、 低HPでほぼ止まる。 base回復 (econ v2=9/s) は実測待ちで据え置き。
  postureRecoveryFloorMul: 0.05,
  // ★2026-07-29 追加: 体幹回復の**段階表** [その幕のHP残率の下限, 回復倍率]。上から順に評価。
  //   SEKIRO 公式マニュアル「体力が低いほど体幹の回復が遅くなる」+ Fextralife の実測表に合わせた。
  //   従来は直線 (floorMul + (1-floorMul)×HP残率) で、**HP残25%でもまだ回復29%** だったため
  //   体幹が固定されるのがバーの底だけ = 実測で全ボスが HP 削り切りになっていた。
  //   最下段 (25%未満) は floorMul を使う。
  //   ⚠️ null にすると従来の直線へ完全に戻る (戻せる形で作る)。
  postureRecoveryCurve: [
    [0.75, 1.00],   // HP残 75%以上 … 回復そのまま = 序盤は体幹が溜まらない
    [0.50, 0.66],   // 75〜50%
    [0.25, 0.33],   // 50〜25%
  ] as ReadonlyArray<readonly [number, number]> | null,
  // ★2026-07-29 追加: 猶予が切れた後、体幹の減衰を 0→全速へ立ち上げる秒数。
  //   崖を作らないための緩衝 (ユーザー裁定「5秒で急にゼロは理不尽」)。
  postureDecayRampSec: 0.35,
  staggerMs: 1400,
  // walkSpeed は プレイヤー後退 (PLAYER.moveSpeed=120) を確実に上回らせる。
  //   旧 140 では逃げる相手に永遠に追いつけず「下がり続けで一方的にスカせる」原因だった。
  //   270 = 後退に対し +150px/s で確実に詰める (獣の追走)。 ※歩き 220→120 の正式採用後も
  //   据え置き (詰めはむしろ速くなった。 体感調整は C-2 のプレイフィードバックで)。
  walkSpeed: 270,
  preferredRange: 150,
  // 攻撃を出してよい間合い。 これより遠ければ telegraph に入らず詰め続ける。
  //   旧実装は間合い無関係に 1 秒で攻撃 → 遠距離から溜め→空振りを繰り返せた。
  //   詰め切ってから溜める = 「遠くから空振り」を構造的に消す。
  attackCommitRange: 185,
  // 攻撃と攻撃の「間」。 固定だとメトロノーム的になるので毎サイクルこの範囲で揺らす
  //   (attackCount の hash 由来 = 決定的だが単調でない)。
  //   2026-06-25 大幅短縮: idle はモーション選択肢のひとつ。プレイヤーは常に対応に追われる設計へ。
  idleGapMinMs: 150,
  idleGapMaxMs: 350,

  // 位置避け不可 (no position-evade) の「踏み込み」設定。 spec 2026-06-02。
  //   方針: 間合い詰めは telegraph の無言スライドではなく「攻撃アクション本体 = active の
  //   踏み込み (lunge)」で行う。 telegraph 中ボスは動かない (= 読みの時間)。
  //   active 突入時にプレイヤー位置を起点に踏み込み量を逆算し、 後退していてもそこへ届かせる。
  //
  //   gapCloseBuffer: 踏み込みの着地点を「range - buffer」にする余裕しろ。
  //     active 開始時に確実に間合い内 (range の内側) へ着地させる。
  //   minLungeFrac: 踏み込み量の下限を lungeDistance のこの割合にする。
  //     プレイヤーが密着していても最低限「踏み込むモーション」を出す (その場ぴょこ防止)。
  gapCloseBuffer: 28,
  minLungeFrac: 0.25,
  // 🔴 間合いのラチェットを外す (2026-07-27)。
  //   ボスの neutral の歩きは「遠ければ詰める」だけで「近すぎれば離れる」が無かった。
  //   踏み込みも常に前進なので、二人の距離は詰まる一方 = 主人公が壁へ押し込まれる
  //   (友達FB「重なっている時間が長い」・実測で壁200px以内の滞在が 78.6%)。
  //   → **neutral の歩きにだけ**「離れる」を足す。
  //   ⚠️ 踏み込み (minLungeFrac) 側は触らない。密着時に踏み込ませない/下がらせる版を実装して
  //     実測したところ、騎士で回避主体の勝ち筋が 20/20 → 0/20、花嫁で 10/20 → 2/20 に消えた。
  //     ボスが密着を維持することが recovery を狩る窓を作っており、そこは load-bearing だった。
  bossBackStep: true,               // 介錯のステップも参照する既存ゲート。通常の連続後退とは分けて維持。
  neutralBackOff: false,            // 09-26: 常時の連続後退のみ停止。立て直し・介錯のステップは維持。旧は DEV ?bossBackStep=on。
  neutralBackOffHysteresisPx: 40,   // 好みの間合いからこれ以上近い時だけ下がる (震え防止)
  neutralBackOffSpeedMul: 0.7,      // 下がる速さ (詰める時より遅い = 逃げではなく間合いの取り直し)
  // 間合いの立て直し (2026-09-18・身体判定なしの補い): 🔵 ユーザー裁定「入れて」(2026-09-18) で通常起動へ採用。
  //   形は「後回しなし」(deferMs 0 = 攻撃のテンポは変えず、待機中に跳び退く動きだけ足す)。旧＝off は ?bossRegain=0、
  //   後回し 0.4 秒の比較は ?regainWait=400。介錯の下がり方は boss.ts overlapRegain.execStyle (既定 walk)。
  //   上の後退は 0.7倍 (騎士 104px/s) で主人公の歩き 120px/s に負ける = 歩いて押し込めば芯に重なり続けられる。
  //   主人公が芯の帯 (芯半幅+15) に overlapRegainHoldMs 以上居続けたら (技の最中に入られた分も数える)、
  //   次の idle で後退を overlapRegainSpeedMul 倍 (跳び退く速さ) にして好みの間合い−ヒステリシスまで離れ、
  //   その間は最大 overlapRegainDeferMs だけ攻撃を後回しにする (0 = 後回しにしない)。
  //   介錯は同じ条件で後退の冷却を待たずに一歩下がる。
  overlapRegain: true,
  overlapRegainHoldMs: 150,
  overlapRegainSpeedMul: 2.0,
  overlapRegainDeferMs: 0,
  // 位置避け不可攻撃の active 中突進の速度上限 (px/s)。
  //   lungeTargetX (active 突入時に確定) へ向かう速度上限。 ワープにならない可視の踏み込み。
  lungeSpeed: 1900,
  // 位置避け不可攻撃の踏み込み上限の「上乗せ分」。 telegraph 中ボスは静止するので、
  //   その間にプレイヤーが後退した分 (chargeMs × moveSpeed ≒ 最大 ~200px) まで
  //   active の踏み込みで取り返せるよう、 lungeDistance に加えて許容する追走距離。
  //   これが無いと長い溜めの間に逃げられ、 短い踏み込み (噛みつき) が届かない。
  //   ※踏み込みは「必要な分だけ」使う適応式なので、 これは天井。 盛りすぎると逃げ切られた時に
  //   画面横断のワープ気味になるため、 walkSpeed の neutral 追走を主、 これを保険にする。
  lungeCatchUp: 120,
  // 後方判定の上限 (px) (#2)。 ボス追従 melee の「後方 (facing と逆側)」リーチをこの距離で頭打ちにする。
  //   ボス半幅55 + プレイヤー半幅15 ≒ 70 が接触距離。100px は「真後ろ張り付き」を被弾させつつ
  //   「遠い後方150-250px」の理不尽を解消するバランス。実機フィールで調整可。
  rearHitRange: 100,
  // motion-warp 攻撃 (forwardWarp 持ち) の踏み込み目標を「固定」する active 進捗 (0..1)。
  //   これ以前 = 後退に追尾 (公平性=届く) / これ以降 = 目標ロック (磁力で吸い付かない=自然な跳びかかり)。
  //   Codex レビュー (2026-06-08): 毎tick追尾は「跳んでるのに後退へ吸着」体感を生む → 序盤で詰め切り中盤以降固定。
  lungeLockProgress: 0.5,
} as const

// 割り込み反撃 (combat-feel-v2 §4.1): 「無心の連打」への応手。 telegraph はフル尺=読める出題。
//   recovery は中断しない (recovery=攻め時 の教育は守る)。 idle の待ちだけを打ち切る。
export const RETALIATION = {
  windowMs: 2500,   // この窓の中で…
  hits: 4,          // …これだけ被弾したら反撃
  cooldownMs: 6000, // 連発しない = 「何発まで殴れるか」の駆け引きを残す
} as const

// 背後応手 (combat-feel-v2 §4.3): 攻撃中に裏へ回られ続けたら、 recovery 明けに振り向き技で応える。
export const BACK_ATTACK = {
  behindThresholdMs: 700,
  // 中心線を少し跨いだだけでは背後滞在と数えない。身体を抜けたことが見て分かる距離から計測する。
  behindDistancePx: 24,
} as const

// プレイヤーとボスの最小距離 (ボス中心からこの距離より内側に踏み込めない)。
// 大型ボス(顔無し戴冠王=見た目 半幅~155px)の「正面=頭」の手前で止まるよう調整。
// プレイヤーの通常攻撃リーチ (PLAYER.attackRange=160) 以下に収め、 攻撃が届くことを担保する。
// ※ 旧値 64 (= 小型ハウンド幅110基準) では大型ボスの頭の中にめり込んで見えた。
export const COLLISION = {
  minBodyDistance: 108,
} as const

// ---- ボスの「芯」(2026-07-27・Nine Sols のボス戦集を見たユーザー観察から) ----
// 観察 (逐語): 「ボスの重心になるところには通れない判定があって、手や足武器などはすり抜けてる感じ。
//   回避で抜けてるように見えた。ボス側が大きい範囲を移動しながら攻撃するようなものは主人公を
//   通り抜けて攻撃していた」
//
// 決めたこと:
//   ① 止まるのは **主人公の歩きだけ**。ボスは何をしていても素通りする (押し返されない)
//   ② 止める範囲は **重心の芯だけ**。腕・脚・武器・マントは通る (見た目の幅で止めない)
//   ③ **回避 (stepping) は通り抜ける** = 背中を取るには回避を1回払う。壁ハメの逃げ道でもある
//
// 🔴 却下済みの 108px の柵 (docs/candidates/2026-07-26-boss-stepin-out-and-back-design §3) と
//    混同しないこと。あれが壊れた原因は **(a) ボスの前進を止めた (向きが逆) (b) 108px は
//    ボスの体 (幅110) より広く「芯」ではなかった** の2つ。ここはどちらもしていない。
//
// 🔵 2026-09-18 ユーザー採用「身体判定なし」: enabled=false が既定。歩き・回避・見切り突進のどれも通り抜け、
//   押し戻し・滑り出しも無い (全ボス共通)。下の芯の説明は旧仕様 (DEV の B キー / ?bodyModel=core で再現)。
//   副作用の番人 = tests/ghost_body_trial.test.ts (不感帯 ±24 に届かない点なし) / tests/combat_sim/ghost_body_probe.test.ts。
// coreHalfWidth 28 + プレイヤー半幅 15 = 歩いて詰められる最短 43px。
//   実測の距離中央値 107px には干渉せず、「4px までめり込む」状態だけが消える。
// 🔴 2026-07-27 追測で判明: 芯へ潜り込む瞬間の **92% が主人公のカウンター突進**
//   (ボスは1% / 回避は8%)。 突進は歩きの 25倍の速さ (1500px/s) で 芯を素通りしていた
//   = 「歩きだけ止める」の穴。 出所=`tests/combat_sim/crossing_attribution.test.ts`
//   stopCounterThrust を false に戻せば 07-27 統合時点の挙動へ戻る (定数1つ)。
export const BOSS_CORE = {
  enabled: false,       // 2026-09-18 採用: 身体判定なし (歩き・回避・突進が通り抜ける)。DEV の B キーで旧・芯ありと A/B できる
  attackStopAtReach: false, // 09-15ユーザー却下。reachはDEVの過去比較専用。通常起動は導入前。
  attackStopMaxGapPx: PLAYER.attackRange, // 共通の停止距離が通常反撃のリーチを超えない上限
  coreHalfWidth: 28,
  playerHalfWidth: 15,
  stopCounterThrust: true,   // 見切りカウンターの突進も芯の手前で止める (回避は従来どおり通す)
  // 🔴 2026-07-27 追測 (花嫁 vowEmbrace): 当たり判定が終わった **recovery の前進**
  //   (AttackDef.recoveryDriftPx) が芯を見ていなかった。 実測: 主人公は芯 41px で完全に
  //   止まっているのに、ボスだけが 2px/tick で 14tick 前進し rel 41px → 13px まで
  //   主人公の体の中へ潜り続けていた。 active (踏み込み) と neutral 歩きは既に
  //   clampToBossCoreFront を通っていて、recovery のドリフトだけ穴が空いていた。
  //   false へ戻すと 07-27 統合時点の挙動へ完全に戻る (定数1つ)。
  //   10-18: 芯より近い (41px 等) 時だけ止めが効かず体を抜けていた穴を塞いだ (boss.ts clampToBossCoreFront の holdInside)。
  stopRecoveryDrift: true,
} as const

// 崩れ中の「舞台照明の絞り」の立ち上がり/引きの尺 (秒)。 2026-07-26 のHUD設計で確定した演出。
//   🔴 ここに置いてあるのは **render.ts と game.ts の両方が同じ値を見る必要がある**ため。
//      稽古の仕上げは崩れを保持するために stateElapsed を止めるが、止める位置をこの値から
//      導かないと「立ち上がる前で止めて照明が永久に出ない」「引きに入って暗くなる」が起きる。
export const STAGGER_SPOT = { inSec: 0.16, outSec: 0.26 } as const

// ボス→プレイヤー処刑の寄せ距離 (px)。 finisherAnchorOffset (プレイヤー→ボス致命用) とは別管理。
// ボス半幅55 + プレイヤー半幅15 ≒ 70 が接触。 55 = やや懐まで詰めた「刺さる」距離 (叩き台: 40〜70 で微調整)。
export const BOSS_EXECUTE_APPROACH_PX = 55

// =================================================================
// 戦闘の根幹デザイン:
//   - 倒し方は HP を 0 にすることだけ (OHK 処刑はナシ)
//   - 通常攻撃 HP ダメは小。普段はチクチクしか減らない (実効値は econ() 側)
//   - パリィで体幹を崩すと「致命の一撃」が打てる: 攻撃ボタンで自動3連撃
//     → 最終ヒットで stagger 強制終了 / スタミナ消費なし (崩し成功のご褒美)
//   🔴 2026-07-15 の段階制 (本物のSEKIRO型) 以降、下の2行は古い (2026-09-05 横断監査 #3):
//     ~~計 40 HP ダメ~~ = 通常致命は最初の2発だけを HP へ乗せ、 最終ヒットは combat.ts の
//       phaseFullHp() で「次の幕の満タン」へ置換する。 hitDamages の合計は撃破必要量ではない。
//     ~~3 回 stagger で削り切れる (ハウンド/曲芸師)~~ = 撃破に要る致命の回数は roomCount (幕の数)
//       が決め、 最終幕は finisher 変種の単発で HP0。 例に出したボスも退役済み。
//       幕ごとのHP = docs/generated/econ-current-values.md (自動生成)。
// =================================================================
export const EXECUTE = {
  hits: 3,
  // 各ヒットの HP ダメ。 最初の2発だけが HP へ乗り、 3発目は次の幕の満タンへ置換される
  //   (combat.ts resolveExecuteHit)。 合計値は「撃破に要る量」ではない。
  hitDamages: [13, 13, 14],
  // 1ヒットのフェーズ長 (windup → active → recovery)
  perHitDurationMs: 240,
  // フェーズ内でヒット判定が出る区間 (フェーズ開始からの相対 ms)
  perHitActiveStartMs: 60,
  perHitActiveEndMs: 130,
  // 最終ヒット後の決めポーズ時間
  endPoseMs: 180,
  // 致命中のリーチ (通常より広め)
  reach: 130,
  // 致命を「開始」できる間合い (これを超えると崩し中でも致命に入れず通常攻撃へ)。
  //   遠距離からの空振り致命 (崩しを無駄撃ち) を防ぐ。 reach と同値=開始できたヒットは必ず届く。
  // 未使用。実際に効いているのは reach と approachRange。
  startRange: 130,
  // 踏み込み致命: startRange より遠くても、この範囲内なら短く踏み込んで致命を出せる (#1)。
  //   最大の parryable ボス攻撃 range (lanceSweep=200) 以上に設定し、
  //   「崩しを取れた間合い = 致命を開始できる間合い」を一致させる。
  //   この上限を超えたら真に離れた扱いで通常攻撃へフォールバック。
  approachRange: 220,
  // トドメ致命の刺し距離 (px)。 ボス毎の finisherAnchorOffset 未指定時のデフォルト (Codex 監査 C)。
  //   密着より「深く刺す」シルエット優先でやや遠め。 大型ボスは boss 側で 120〜130 に上書き。
  defaultFinisherAnchor: 120,
  // 致命突入時、 刺し距離(anchor)へ寄せる吸着時間 (ms)。 F0〜F6 の間に短く寄せ、 以降は動かさない (足滑り防止)。
  anchorEaseMs: 120,
} as const

// 「絵の縁」を測る起点にする (2026-08-11)。出所 = docs/candidates/2026-08-11-hitbox-body-edge-codex-ticket.md
//   人型ボスの描かれた体の半幅 ≒ 60px で、現行の各定数はこの体型で調整されている:
//     PLAYER.attackRange 160 = 60 + 100  (100 = 体の縁から先へ伸びる剣の長さ)
//     EXECUTE.reach      130 = 60 +  70
//   四つ足の獣 (王152 / 覚醒王157) は体が判定をほぼ丸ごと埋め、「絵では当たっているのに
//   当たらない」が出ていた。余白 (100 / 70) は据え置きで、測る起点だけ中心 → 体の縁へ移す。
//   ⚠️ 半幅は絵を差し替えたら測り直す = scripts/measure_boss_body.py
export const BODY_EDGE_BASELINE_HALF_W = 60

export function playerAttackReachFor(bodyHalfWidthPx: number): number {
  return Math.max(
    PLAYER.attackRange,
    PLAYER.attackRange - BODY_EDGE_BASELINE_HALF_W + bodyHalfWidthPx,
  )
}

export function executeReachFor(bodyHalfWidthPx: number): number {
  return Math.max(
    EXECUTE.reach,
    EXECUTE.reach - BODY_EDGE_BASELINE_HALF_W + bodyHalfWidthPx,
  )
}

// 致命を「開始できる」間合いは、必ず「届く」間合い以上でなければならない。
export function executeApproachRangeFor(bodyHalfWidthPx: number): number {
  return Math.max(EXECUTE.approachRange, executeReachFor(bodyHalfWidthPx))
}

// 致命・撃破の体感パラメータ (spec 2026-06-01-execute-finisher-juice)。
// 実機で触って詰める前提の叩き台。 ms 単位。
export const EXECUTE_FX = {
  // 通常致命 (普通に気持ちいい基準)
  normalFinalHitStopMs: 200,
  normalFinalShake: { amplitude: 10, durationMs: 320 },
  // トドメ致命の「単一」hitStop (ms)。 通常致命の最終ヒット(220)を二重掛けせず、 これ1つに集約してから
  //   pullTimeScale の魂吸収スローへ入る (Codex 監査 D)。 体感は実機で 140〜170 を目安に微調整。
  finisherHitStopMs: 155,
  // トドメ専用 (上乗せで格上)
  anticipationMs: 100,
  anticipationTimeScale: 0.55,
  impactHitStopMs: 150,
  impactShake: { amplitude: 14, durationMs: 280 },
  impactFlashMs: 90,
  chromaticAberrationPx: 4,
  pullDurationMs: 420,
  pullTimeScale: 0.4,
  absorbFlashMs: 240,
  dissolveMs: 420,
  silenceBeatMs: 1600,        // 撃破後の静寂(残光の余韻、 この後 victory へ)
} as const

// ボス処刑 (確殺フィニッシャー) 専用の打点体感。 通常被弾(COMBAT.hit)より重い「処刑」を演出する。
//   崩し気絶 → 処刑命中の瞬間 (active の contact) に乗せる。 配分=振り抜き(打点) が主役。
//   Codex 監査 2026-06-10: contact hitStop は 120-160ms (220+ は「止まりすぎ」) / shake ≤16 (3D酔い配慮)。
//   ★2026-06-21 #2 リワーク: 死=破壊(赤い爆発) → 死=浄化(紫の魂が常夜燈へ還る) へ作り替え。
//   打点の重さ (hitStop/shake/白フラッシュ=「1拍の痛み」) は維持し、 余波の色だけ赤→紫の浄化へ。
//   (10-11 から浄化は青白 SOUL_BLUE。 当たった瞬間の閃光の紫の縁だけはボスの致命の色として残す＝10-11 朝の答え「残す」)
//   ボス撃破の浄化 (drawFinisherSoul) と対だが「誰も彼女を解放しない」= 魂は吸収されず常夜燈へ昇る。
export const BOSS_EXECUTE_FX = {
  impactHitStopMs: 150,
  impactShake: { amplitude: 16, durationMs: 360 },   // 揺れは上限(16, 3D酔い配慮) + 長めで重く
  impactFlashMs: 120,
  // 処刑シネマティック (deathBeat.execution 中の全画面演出)。 プレイヤー致命と同格の見せ場へ。
  //   #2: 赤白フラッシュ+色収差(破壊)を撤去 → 冷白の一閃(1拍の凍結)→紫の薄いウォッシュ(浄化) へ。
  cinematicFlashMs: 200,       // 冷白の一閃の尺 (hitStop 中は elapsed 停止 = 凍結フラッシュ=1拍の痛み)
  vignetteMaxAlpha: 0.74,      // 締まる暗ヴィネットの最大濃さ (死の枠)
  vignetteRampMs: 720,         // ヴィネットが締まりきるまで
  // 死の浄化 (#2/#9): LUNAIRE 自身の魂 (10-11 から青白 SOUL_BLUE。 旧は紫 #9a7ac4) が体からほどけ、 昇って常夜燈へ還る。
  //   deathBeat 窓 (≤1150ms) に収める = 前半に密に放出し、 各粒が短く昇って常夜燈で消える。
  purifyStartMs: 150,          // 白の一拍が引いてから魂が剥がれ始める (痛み→浄化の段差をつくる)
  purifyEmitMs: 440,           // 魂を放出し続ける窓 (前半に密集 = 体が一気にほどける)
  purifyTravelMs: 560,         // 各粒が体→常夜燈へかかる時間
  purifyMoteCount: 30,         // 放出する魂の粒数
} as const

// プレイヤーへのダメージ量 (HP100 スケール)。
//   standard: 通常ボス攻撃1発 = 命灯1個分 (20HP)。
//   finisher: ボス処刑1発 = 命灯3個分 (60HP)。
//   knight*: 騎士技ごとの個別値。役割の重みに合わせて 22〜40 に差をつける。
export const PLAYER_DAMAGE = {
  standard: 20,
  finisher: 60,
  knightSweep: 24,   // 境界の薙ぎ: 基本パリィの失敗罰 (標準+4)
  knightThrust: 35,  // 宣告の突き: 赤/見切り要求の主教材 (重め)
  knightButt: 22,    // 石突の返し: 密着咎め (軽め・位置を直させるだけ)
  knightCross: 24,   // 境界の薙ぎ下がり: 壁際リセット弾き技 (knightSweep と同値)
} as const

export const COMBAT = {
  // 通常攻撃: HP はチクっと、体幹そこそこ
  playerAttack: {
    bossHpDamage: 2,
    bossPostureGain: 4,   // 攻撃=チップ削り。 崩しの主軸はパリィ(25)。 殴り連打で崩せないよう抑制
    // P2 経済 (ブループリント §4.2「技後の隙と崩し中だけ刃が通る」):
    bossHpDamageOpen: 3,  // recovery/staggered 中の被弾 = 攻め時の報酬 (chip2 より太く)。⚠️ v1 の値。今の経済 v2 (src/econ.ts) では開きの HP も 2＝通常と同じで、上乗せは体幹 (10 対 4) だけ (10-14 確認・combat.ts の開きの注記)
    // G-5機構 (spec 2026-06-12 §G-5): 開き (recovery/staggered) を正しく読んで刺した時だけ
    //   体幹を太く削る "器"。 通常 bossPostureGain(4) を流用していたのを分離した。
    //   ⚠️ ドラフト値 = 8 (spec 提案 +8〜10 の下限)。 H-1 (ユーザー通しプレイ) 後に確定する
    //   (spec §0 原則2「G体感の数値確定は H-1 後」/ §G-5)。 現状は機構+暫定値で bot 計測まで。
    bossPostureGainOpen: 8,
    guardPostureGain: 2,  // v1 経済での説明: 満HP回復(7/s)が連打のガード加算を食う=満タンでは殴りで割れない(SEKIRO型)。
                        // v2 では postureRegenLockSec がガード加算でも回復を止めるため、蓄積は残るが
                        // 崩しは combat.ts の !isGuarded ガードで防止される。⚠️ドラフト・H-1再テスト
    // 通常命中の停止。固定更新の描画保証と組み合わせ、浅い命中でも「刃が止まった」と読める3〜4fを作る。
    hitStopMs: 60,
    // recovery / staggered へ刺した深い命中。通常より一段長いが、カウンター以上にはしない。
    deepHitStopMs: 90,
    // 連撃3段目 (重い締め) の命中。浅い命中も「刺さった」と読める長さへ、深い命中はカウンターより少し長い。
    heavyHitStopMs: 80,
    heavyDeepHitStopMs: 100,
    shake: { amplitude: 2, durationMs: 100 },
  },
  // カウンター攻撃 (ジャストドッジ/見切り後)。
  //   2026-06-11 部屋制協議: 「HPバースト」から「体幹のご褒美」へ振替。
  //   回避ボス (花嫁) の崩し主軸 = 見切り反撃。 HP を大きく減らす役は致命 (部屋を閉じる)。
  counterAttack: {
    bossHpDamage: 6,
    bossPostureGain: 16,
    hitStopMs: 90,
    shake: { amplitude: 5, durationMs: 200 },
  },
  // パリィ成功: 体幹崩しの主軸
  parrySuccess: {
    bossPostureGain: 25,              // ジャストパリィのボス体幹増加
    normalParryBossPostureGain: 12,  // 通常パリィのボス体幹増加 (ジャストの約半分)
    selfPostureGain: 4,              // 旧値 (未使用・後続掃除で削除予定)
    hitStopMs: 150,
    shake: { amplitude: 6, durationMs: 240 },
  },
  // 連続弾きエスカレーション (P4 §6.1・視覚専用): windowMs 以内に弾き続けると段が上がる (cap 上限)。
  //   P4.5 (Fable審美検収): 1段と5段が静止画でも一目で違うよう係数を引き上げ。
  //   5段で spark×2.0 / pop×1.88。 火花は色温度シフト(銀→白金)も併用で「大きい+熱い」の二重。
  parryChain: {
    windowMs: 1400, cap: 5,
    sparkBoostPerStep: 0.25,   // 火花強度の1段あたり増分 (hits.ts chainBoost)。5段で intensity×2.0
    popBoostPerStep: 0.22,     // HUD pop の1段あたり増分 (render.ts escal)
    sparkIntensityMax: 2.0,    // 火花 intensity の上限 (drawParrySpark のクランプ上限と一致)
    tactileBoostPerStep: 0.12, // hitStop/シェイクの1段あたり増分 (5段で ×1.48)
    // ★2026-07-29 追加: **ボス体幹ゲインの1段あたり増分** (hits.ts chainPostureMul)。
    //   SEKIRO は「連続で弾くほど敵の体幹ダメージが大きくなる」(Fextralife/Posture)。
    //   ここが 0 だった (=火花だけ派手で報酬は増えない) ため、連撃を弾き切る意味が数値に無く、
    //   実測で全ボスが HP 削り切りになっていた。0.25 で 5段目 ×2.0。
    //   ⚠️ 0 にすると従来の挙動へ完全に戻る (戻せる形で作る)。
    posturePerStep: 0.25,
  },
  hit: {
    playerHpLoss: PLAYER_DAMAGE.standard,
    playerPostureGain: 28,
    hitStopMs: 70,
    shake: { amplitude: 9, durationMs: 280 },
  },
  // 体幹崩しの瞬間の演出
  stagger: {
    shake: { amplitude: 10, durationMs: 520 },
  },
  // staggered 中の攻撃ヒット (火力倍率窓の中で当たった時の演出)
  staggerHit: {
    hitStopMs: 120,
    shake: { amplitude: 6, durationMs: 220 },
  },
} as const

// チュートリアル (名も無き亡霊) の稽古モード: 1技あたり何回正しく対応できたら次の技へ進むか。
export const TUTORIAL = {
  successesPerLesson: 3,
} as const

export const TELEGRAPH = {
  cueAtPercent: 0.30,
  hintMarkerPercent: 0.70,
} as const

// テーマカラー（夜のネオン遊園地）
export const PALETTE = {
  bgDeep: '#06030d',
  bgMid: '#1a0a2e',
  neonViolet: '#a050ff',
  neonPink: '#ff72cb',
  neonCyan: '#7afff5',
  // パリィスパークは「淡い暖色＋シアン」のミックス。白飛びを避ける
  parrySparkWarm: '#ffe9b8',
  parrySparkCool: '#7afff5',
  // ゴシック FX (現主人公に合わせた演出。 spec 2026-05-31-fx-protagonist-aesthetic)
  //   紫=魔/パリィ, 銀=刃のclash, 金=致命の差し。 旧 neon は残置(他描画が参照中)
  fxViolet: '#9a7ac4',
  fxVioletBright: '#c8b0ec',
  fxSilver: '#dcdcdc',
  fxGold: '#f4d878',
  hpBar: '#ff5a8a',
  postureBar: '#a050ff',
  postureBarBoss: '#ff72cb',
  // 戦闘HUD (2026-06-01 リスタイル: アニメ調セル・四角。 spec 2026-06-01-combat-hud-restyle)
  //   赤=命 / 金=体幹 (敵味方共通) / 氷青=行動力(スタミナ)。 暗縁+ツヤ帯でキャラと同じ筆致。
  //   ⚠️ 1色=1意味原則 (D1 2026-06-15) は **2026-07-22 に更新**。
  //     旧: 「金はボス体幹のみ=崩す対象=主役。自分の体幹は銅紫で別系統」
  //     新: 「金=体幹 (敵味方とも)。ただしボス側を明るい月金・自分側を一段暗い燻し金にして主役感は保つ」
  //     理由 (ユーザー実機 2026-07-22): 旧配色では **ボスの"体力"(淡い青白) と 自分の"体幹"(淡い紫) が
  //     似ていて紛らわしく**、逆に **同じ「体幹」なのに敵味方で色が違って** 学習を妨げていた。
  //     SEKIRO は敵味方とも体幹を同系色にしている。 色=意味 で覚えられる方を優先する。
  hudHp: '#a83a3a',             // 旧/非推奨: 赤HPバーはボス下端HUDでは使わない (2026-06-18 HUD方針)
  hudHpHi: 'rgba(255,210,200,0.6)',  // 旧/非推奨
  hudHpTrack: '#3a161c',       // 旧/非推奨: 空きHP暗赤 (現在はボスHPに象牙/銀白を使用)
  hudPosture: '#f4d878',       // ボス体幹専用 (金=崩しゲージ)
  hudPostureTrack: '#3a3014',  // 空きボス体幹: 暗い金
  // 自分体幹 = 燻し金 (ボスの月金 #f4d878 より暗い)。 崩れが近づくと下の3段へ寄る。
  hudPlayerPosture: '#c9a44e',      // 平常 (燻し金)
  hudPlayerPostureWarn: '#e08a3c',  // 警戒 (橙・75%〜)
  hudPlayerPostureDanger: '#e0503a', // 危険 (朱・90%〜=あと一撃で崩れる)
  hudPlayerPostureTrack: '#2b2110', // 空き自分体幹: 暗い金
  // 命灯 (ロウソク) の炎の色。 体幹バーとは別系統 (体幹を金へ移したのでここは紫のまま残す)。
  hudLifeFlameMid: '#b9a8e0',
  // ※ hudStamina* は 2026-07-27 に撤去 (スタミナ廃止・バーは 2026-06-17 に既に消えていた)
  hudOutline: '#180c14',
  hudGloss: 'rgba(255,255,255,0.34)',
  hudGroove: 'rgba(10,8,14,0.6)',
  hudGold: '#f4d878',
  hudScrim: 'rgba(8,5,12,0.5)',
  textDim: '#8a6dbf',
  textBright: '#e8d8ff',
  danger: '#ff3a6e',
  safe: '#7afff5',
  // プレイヤー（ゴシック・紫）
  playerSkin: '#f5e6d5',
  playerHair: '#1c0d2e',
  playerHairHi: '#5b2d80',
  playerDress: '#2a1240',
  playerDressTrim: '#a050ff',
  playerSpear: '#cdb8ff',
  playerSpearTip: '#7afff5',
  // ハウンド（壊れた機械獣）
  houndBody: '#1f0d35',
  houndPlate: '#3a1a55',
  houndSeam: '#a050ff',
  houndEye: '#ff5fc8',
  houndSpark: '#ffd7f2',
} as const

// ほどける光の色 = 「灯より白い青白」 (2026-10-11 ユーザー裁定 purple-soul-blue=2 / purple-death-soul=①)。
//   常夜燈の青 (#aacdff / #608ee4) より白く、いちばん青いのは常夜燈のまま。 紫は「ボスの致命・パリィでしか
//   返せない攻撃」の合図に取っておく。 撃破の魂 (drawFinisherSoul)・撃破の霧と光片 (drawFinisherBurst)・
//   灰の粒 (drawFinisherMotes)・主人公の死の浄化 (drawDeathPurification)・終幕A (drawLampRiver) が使う。
//   旧の色 (紫): 魂の頭 #cdb0f0 / ほぼ白 #eadcff / 粒 #dcc8f4 / 霧 #cdc2e8 #bcb2d8 / 核 #c8b0ec / 閃光 #c8b0ec /
//   光片 #d8cef4 / 満ちる光 #dfd8f5 #bfb6e0 / 死の浄化 #9a7ac4 #c8b0ec #efe2ff / 光の川 rgba(150,110,220) rgba(170,130,240)。
//   旧の色へ戻す時はここを使わず、上の値を各描画へ書き戻す (比較スイッチは持たない=答え済み)。
export const SOUL_BLUE = {
  head: '#c6d8f4',        // 魂の頭・主人公の浄化の本体
  tail: '#e8f0ff',        // 魂の尾・芯の白
  haze: '#c4cade',        // 魔法陣・霧 (銀青)
  hazeLight: '#c6d2e6',   // 魔法陣・霧 (銀青・やや明るい)
  white: '#e8f0ff',       // 冷白 (閃光・満ちる光の中心)
} as const

// 崩れた時 (stunned) の頭の上の火 = 白っぽい灰 (2026-10-11 purple-stun-mote=②)。 紫・青には寄せない。
//   旧の色は PALETTE.fxViolet (#9a7ac4)。 紫は「ボスの溜め」だけにする (崩れ中はボスの致命が来る)。
export const STUN_ASH = {
  light: '#cfcac4',
  dark: '#8f8a86',
} as const
